/**
 * The audio rig the in-house games play through.
 *
 * Every sound in here is **synthesised** rather than loaded from a file. That
 * is partly weight - twenty-one games' worth of samples is megabytes of audio
 * on a page that otherwise ships none - but mostly it is about retriggering. A
 * plinko ball strikes fourteen pegs on the way down, and one sample fired
 * fourteen times reads as a stuck key; a filtered noise burst detuned by the
 * row reads as a ball falling. The same goes for a keno card filling up, a
 * mines board being picked apart, and a crash multiplier climbing: the games
 * here make sound *continuously*, out of numbers that are already to hand,
 * which is what an oscillator is good at and a sample is not.
 *
 * This module owns the plumbing only - the context, the bus, the envelope
 * primitives. What the sounds actually are lives in `voices.ts`, and the entry
 * point the app calls lives in `index.ts`.
 */

/**
 * Where the mix sits under the page.
 *
 * The games are loud in aggregate - plinko on auto is a near continuous
 * rattle - so the ceiling is deliberately low and the player's own volume
 * scales inside it rather than above it.
 */
const MASTER_CEILING = 0.5;

/** Seconds of white noise kept around to carve impacts out of. */
const NOISE_SECONDS = 2;

export interface Rig {
  ctx: AudioContext;
  /** Everything connects here; the player's volume rides on it. */
  bus: GainNode;
  /** One buffer of white noise, read from a random offset per hit. */
  noise: AudioBuffer;
}

interface Settings {
  muted: boolean;
  /** 0–1, the player's own level inside the ceiling. */
  volume: number;
}

const settings: Settings = { muted: false, volume: 0.6 };

let rig: Rig | null = null;
/** Set once the browser has told us it cannot give us audio at all. */
let unavailable = false;

const audioContextCtor = (): typeof AudioContext | undefined => {
  if (typeof window === "undefined") return undefined;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
};

/** The level the bus should be at, given mute and the player's volume. */
const busLevel = () =>
  settings.muted ? 0 : MASTER_CEILING * Math.max(0, Math.min(1, settings.volume));

const buildNoise = (ctx: AudioContext) => {
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * NOISE_SECONDS), ctx.sampleRate);
  const channel = buffer.getChannelData(0);
  for (let i = 0; i < channel.length; i += 1) channel[i] = Math.random() * 2 - 1;
  return buffer;
};

/**
 * The rig, built on first use.
 *
 * A context created before the player has touched the page starts suspended,
 * so this never assumes it is running - `resume()` is cheap when it already is,
 * and `primeSound` arranges for the first gesture to wake it either way.
 */
export const getRig = (): Rig | null => {
  if (rig) {
    if (rig.ctx.state === "suspended") void rig.ctx.resume();
    return rig;
  }
  if (unavailable) return null;

  const Ctor = audioContextCtor();
  if (!Ctor) {
    unavailable = true;
    return null;
  }

  try {
    const ctx = new Ctor();
    const bus = ctx.createGain();
    bus.gain.value = busLevel();

    // A limiter in all but name. Fourteen peg hits inside half a second add up
    // past full scale on their own, and a clipped board sounds broken rather
    // than busy - so the mix is squeezed on the way out instead of being
    // quietened to the point where a single click is inaudible.
    const squeeze = ctx.createDynamicsCompressor();
    squeeze.threshold.value = -16;
    squeeze.knee.value = 24;
    squeeze.ratio.value = 10;
    squeeze.attack.value = 0.003;
    squeeze.release.value = 0.18;

    bus.connect(squeeze);
    squeeze.connect(ctx.destination);

    rig = { ctx, bus, noise: buildNoise(ctx) };
    void ctx.resume();
    return rig;
  } catch {
    // An iframe with no autoplay permission, or a browser out of contexts.
    // Either way there is no recovering, and a silent game is a far better
    // outcome than a game that throws on every click.
    unavailable = true;
    return null;
  }
};

/** Whether anything would be heard, so callers can skip the work entirely. */
export const isAudible = () => !settings.muted && settings.volume > 0 && !unavailable;

/** Point the rig at the player's current preferences. */
export const applySettings = (next: Settings) => {
  settings.muted = next.muted;
  settings.volume = next.volume;

  if (!rig) return;
  // Ramped rather than set: a mute that lands mid-note would click.
  const now = rig.ctx.currentTime;
  rig.bus.gain.cancelScheduledValues(now);
  rig.bus.gain.setValueAtTime(rig.bus.gain.value, now);
  rig.bus.gain.linearRampToValueAtTime(busLevel(), now + 0.05);
};

/**
 * Wake the rig on the player's first gesture.
 *
 * Not every sound here is fired from a click - a keno draw and a mines bust
 * both land out of a timer, some way after the press that started them - so
 * waiting for a gesture-borne `play()` to build the context would lose the
 * first round's audio on a cold page. This takes the first press the page sees,
 * whatever it was for, and uses it to get the context running.
 */
export const primeSound = () => {
  if (typeof window === "undefined" || unavailable) return;

  const wake = () => {
    getRig();
    window.removeEventListener("pointerdown", wake);
    window.removeEventListener("keydown", wake);
    window.removeEventListener("touchstart", wake);
  };

  window.addEventListener("pointerdown", wake, { once: true, passive: true });
  window.addEventListener("keydown", wake, { once: true });
  window.addEventListener("touchstart", wake, { once: true, passive: true });
};

/**
 * An attack-then-decay envelope on a gain.
 *
 * Exponential on the way down because that is how things actually stop
 * sounding; a linear fade on a short hit reads as a note being cut off. The
 * floor sits a hair above zero since an exponential ramp cannot reach it.
 */
const shape = (param: AudioParam, at: number, peak: number, attack: number, decay: number) => {
  param.setValueAtTime(0.0001, at);
  param.linearRampToValueAtTime(Math.max(peak, 0.0002), at + attack);
  param.exponentialRampToValueAtTime(0.0001, at + attack + decay);
};

export interface ToneSpec {
  /** Where the note starts, in hertz. */
  freq: number;
  /** Where it ends up, for a note that slides. */
  to?: number;
  type?: OscillatorType;
  /** Seconds from silence to full. A few milliseconds is a click. */
  attack?: number;
  /** Seconds back to silence. */
  decay?: number;
  gain?: number;
  /** Seconds to wait first, for the notes of a chord or the beats of a roll. */
  delay?: number;
  /** A low-pass over the top, for taking the edge off a square or a saw. */
  cutoff?: number;
}

/** One note. */
export const tone = (rig: Rig, at: number, spec: ToneSpec) => {
  const {
    freq,
    to,
    type = "sine",
    attack = 0.004,
    decay = 0.12,
    gain = 0.3,
    delay = 0,
    cutoff,
  } = spec;

  const start = at + delay;
  const span = attack + decay;

  const osc = rig.ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(Math.max(freq, 1), start);
  if (to !== undefined && to !== freq) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(to, 1), start + span);
  }

  const env = rig.ctx.createGain();
  shape(env.gain, start, gain, attack, decay);
  osc.connect(env);

  let tail: AudioNode = env;
  if (cutoff) {
    const lp = rig.ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = cutoff;
    env.connect(lp);
    tail = lp;
  }
  tail.connect(rig.bus);

  osc.start(start);
  osc.stop(start + span + 0.02);
};

export interface NoiseSpec {
  /** How the noise is carved: a band is a knock, a high-pass is a hiss. */
  type?: BiquadFilterType;
  freq?: number;
  /** Sweep the filter, for a whoosh rather than a knock. */
  to?: number;
  /** How narrow the band is. High is pitched and woody, low is broad. */
  q?: number;
  attack?: number;
  decay?: number;
  gain?: number;
  delay?: number;
}

/** One impact, or one breath of air. */
export const noise = (rig: Rig, at: number, spec: NoiseSpec = {}) => {
  const {
    type = "bandpass",
    freq = 900,
    to,
    q = 1,
    attack = 0.002,
    decay = 0.09,
    gain = 0.3,
    delay = 0,
  } = spec;

  const start = at + delay;
  const span = attack + decay;

  const source = rig.ctx.createBufferSource();
  source.buffer = rig.noise;
  // A fresh window of the buffer per hit, so two knocks in a row are not the
  // same knock twice - which is the tell that gives a loop away.
  const offset = Math.random() * Math.max(NOISE_SECONDS - span - 0.1, 0);

  const band = rig.ctx.createBiquadFilter();
  band.type = type;
  band.frequency.setValueAtTime(Math.max(freq, 20), start);
  band.Q.value = q;
  if (to !== undefined && to !== freq) {
    band.frequency.exponentialRampToValueAtTime(Math.max(to, 20), start + span);
  }

  const env = rig.ctx.createGain();
  shape(env.gain, start, gain, attack, decay);

  source.connect(band);
  band.connect(env);
  env.connect(rig.bus);

  source.start(start, offset, span + 0.05);
  source.stop(start + span + 0.05);
};

/** A held note that can be bent while it sounds, and let go of afterwards. */
export interface Drone {
  /** Bend to a new pitch over `seconds`. */
  glide: (freq: number, seconds?: number) => void;
  /** Change how loud it is holding, over `seconds`. */
  level: (gain: number, seconds?: number) => void;
  /** Let it go, over `release` seconds. Safe to call twice. */
  stop: (release?: number) => void;
}

export type DroneSpec = Omit<ToneSpec, "to" | "decay" | "delay">;

/**
 * A note that holds until something ends it.
 *
 * For the games whose stage is one continuous event rather than a run of
 * discrete ones: a crash round climbing, a limbo rocket on its way up. Both
 * want a pitch that tracks a number on screen, and a run of pitched clicks at
 * sixty frames a second is not that - a glide is.
 */
export const drone = (rig: Rig, spec: DroneSpec): Drone => {
  const { freq, type = "triangle", attack = 0.08, gain = 0.18, cutoff } = spec;
  const at = rig.ctx.currentTime;

  const osc = rig.ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(Math.max(freq, 1), at);

  const env = rig.ctx.createGain();
  env.gain.setValueAtTime(0.0001, at);
  env.gain.linearRampToValueAtTime(Math.max(gain, 0.0002), at + attack);
  osc.connect(env);

  let tail: AudioNode = env;
  if (cutoff) {
    const lp = rig.ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = cutoff;
    env.connect(lp);
    tail = lp;
  }
  tail.connect(rig.bus);
  osc.start(at);

  let stopped = false;

  return {
    glide: (next, seconds = 0.08) => {
      if (stopped) return;
      const now = rig.ctx.currentTime;
      osc.frequency.cancelScheduledValues(now);
      osc.frequency.setValueAtTime(osc.frequency.value, now);
      osc.frequency.exponentialRampToValueAtTime(Math.max(next, 1), now + Math.max(seconds, 0.01));
    },
    level: (next, seconds = 0.08) => {
      if (stopped) return;
      const now = rig.ctx.currentTime;
      env.gain.cancelScheduledValues(now);
      env.gain.setValueAtTime(env.gain.value, now);
      env.gain.linearRampToValueAtTime(Math.max(next, 0.0002), now + Math.max(seconds, 0.01));
    },
    stop: (release = 0.12) => {
      if (stopped) return;
      stopped = true;
      const now = rig.ctx.currentTime;
      env.gain.cancelScheduledValues(now);
      env.gain.setValueAtTime(env.gain.value, now);
      env.gain.exponentialRampToValueAtTime(0.0001, now + release);
      osc.stop(now + release + 0.02);
    },
  };
};
